import { useState, useRef, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ChevronLeft,
  GripVertical,
  Copy,
  Trash2,
  Smartphone,
  Eye,
  Send,
  Save as SaveIcon,
  Undo2,
  Redo2,
  X,
  ChevronRight,
  ChevronLeft as ChevronLeftIcon,
  ChevronUp,
  ChevronDown,
  Type,
  Heading,
  Image as ImageIcon,
  Square,
  Minus,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Upload,
  Loader2,
  Layers,
  Settings,
  Users,
  Clock,
  Target,
} from "lucide-react";

// ─── API ─────────────────────────────────────────────────────────────────────

const API_URL = `${process.env.REACT_APP_API_URL}/in-app-messages`;

const getHeaders = () => ({
  "x-api-key": localStorage.getItem("api-key"),
  "x-access-token": localStorage.getItem("customer-portal"),
});

const MAX_IMAGE_SIZE_MB = 5;
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];

function validateImageFile(file: File): string | null {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) return "Please upload a JPG, PNG, GIF, or WEBP image.";
  if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) return `Images must be under ${MAX_IMAGE_SIZE_MB}MB.`;
  return null;
}

async function uploadMediaFile(file: File): Promise<string> {
  const formData = new FormData();
  formData.append("file", file);
  const response = await axios.post(`${process.env.REACT_APP_API_URL}/media-upload`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
      "x-access-token": localStorage.getItem("customer-portal"),
      "x-api-key": localStorage.getItem("api-key"),
    },
  });
  const result = response.data;
  if (!result?.success) throw new Error(result?.message || "Upload failed");
  return result.fileUrl || result.url || result.data?.url;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type MessageType = "modal" | "banner" | "slide-in" | "fullscreen";
type Device = "ios" | "android";
type BlockType =
  | "heading"
  | "text"
  | "image"
  | "button"
  | "divider"
  | "spacer";

interface BlockContent {
  text?: string;
  fontSize?: number;
  fontWeight?: "normal" | "medium" | "semibold" | "bold";
  color?: string;
  alignment?: "left" | "center" | "right";
  lineHeight?: number;
  imageUrl?: string;
  imageAlt?: string;
  imageHeight?: number;
  buttonStyle?: "filled" | "outline" | "ghost";
  buttonColor?: string;
  buttonTextColor?: string;
  buttonRadius?: number;
  action?: "openUrl" | "deepLink" | "dismiss";
  actionUrl?: string;
  actionFallbackUrl?: string;
  spacerHeight?: number;
  dividerColor?: string;
  thickness?: number;
}

interface Block {
  id: string;
  type: BlockType;
  content: BlockContent;
}

interface MessageStyle {
  background: string;
  cornerRadius: number;
  backdropOpacity: number;
  animation: string;
  position: string;
  // Dismiss group (BUG-02) — which exit paths the surface offers.
  closeButton: boolean;      // visible close (X)
  tapBackdrop: boolean;      // tap the dimmed backdrop to close (modal only)
  swipeToDismiss: boolean;   // swipe to close
  autoDismissSecs: number;   // 0 = off; banner/slide-in only
}

interface TriggerSettings {
  event: string;
  delay: number;
  delayUnit: string;
}

interface AudienceSettings {
  type: "all" | "cohort";
  cohortId?: string;
  cohortName?: string;
  estimatedReach?: number;
}

interface FrequencySettings {
  type: "once" | "session" | "max";
  maxTimes?: number;
  startDate: string;
  endDate: string;
}

interface GoalSettings {
  event: string;
  window: number;
  windowUnit: "hours" | "days";
}

// ─── Helper ──────────────────────────────────────────────────────────────────

const uid = () => Math.random().toString(36).substr(2, 9);

// ─── Color Palettes ──────────────────────────────────────────────────────────

const TEXT_COLORS = ["#1A202C", "#2D3748", "#4A5568", "#718096", "#A0AEC0", "#FFFFFF", "var(--brand-primary)", "#38A169", "#E53E3E", "#D69E2E"];
const BG_COLORS = ["#FFFFFF", "#F7FAFC", "#EBF8FF", "#F0FFF4", "#FFF5F5", "#FFFAF0", "#1A202C", "#2D3748", "var(--brand-primary)", "#38A169"];
const ACCENT_COLORS = ["var(--brand-primary)", "#38A169", "#E53E3E", "#D69E2E", "#7C3AED", "#DD6B20", "#319795", "#D53F8C", "#1A202C", "#718096"];

// ─── Templates ───────────────────────────────────────────────────────────────

const TEMPLATES: Record<string, { name: string; description: string; type: MessageType; blocks: Block[] }> = {
  welcome: {
    name: "Welcome",
    description: "Onboard new users with a warm greeting",
    type: "modal",
    blocks: [
      { id: uid(), type: "image", content: { imageUrl: "", imageAlt: "Welcome", imageHeight: 140 } },
      { id: uid(), type: "heading", content: { text: "Welcome to Apex Bank!", alignment: "center", fontWeight: "bold", color: "#1A202C", fontSize: 20 } },
      { id: uid(), type: "text", content: { text: "Your account is ready. Let's take a quick tour and set up your preferences.", alignment: "center", color: "#718096", fontSize: 14 } },
      { id: uid(), type: "button", content: { text: "Explore dashboard →", buttonStyle: "filled", buttonColor: "var(--brand-primary)", buttonTextColor: "#FFFFFF", buttonRadius: 12, action: "dismiss" } },
    ],
  },
  announcement: {
    name: "Announcement",
    description: "Highlight a new feature or update",
    type: "modal",
    blocks: [
      { id: uid(), type: "image", content: { imageUrl: "", imageAlt: "Announcement", imageHeight: 120 } },
      { id: uid(), type: "heading", content: { text: "Instant Transfers are here", fontWeight: "bold", color: "#1A202C", fontSize: 18 } },
      { id: uid(), type: "text", content: { text: "Send money to anyone in seconds. Works 24/7, including weekends.", color: "#4A5568", fontSize: 14 } },
      { id: uid(), type: "button", content: { text: "Try it now →", buttonStyle: "filled", buttonColor: "#38A169", buttonTextColor: "#FFFFFF", buttonRadius: 12, action: "openUrl" } },
    ],
  },
  promo: {
    name: "Promo Offer",
    description: "Drive upgrades with a compelling offer",
    type: "modal",
    blocks: [
      { id: uid(), type: "image", content: { imageUrl: "", imageAlt: "Promo", imageHeight: 140 } },
      { id: uid(), type: "heading", content: { text: "Upgrade to Premium", fontWeight: "bold", alignment: "center", color: "#1A202C", fontSize: 18 } },
      { id: uid(), type: "text", content: { text: "Unlock unlimited transfers, zero fees, and priority support.", alignment: "center", color: "#718096", fontSize: 14 } },
      { id: uid(), type: "button", content: { text: "Get 3 months free", buttonStyle: "filled", buttonColor: "var(--brand-primary)", buttonTextColor: "#FFFFFF", buttonRadius: 12, action: "openUrl" } },
    ],
  },
  update: {
    name: "App Update",
    description: "Notify users of a pending update",
    type: "slide-in",
    blocks: [
      { id: uid(), type: "heading", content: { text: "Update available — v3.8.1", fontWeight: "bold", color: "#1A202C", fontSize: 13 } },
      { id: uid(), type: "text", content: { text: "Security patch • 12 MB • What's new: 🔒 Fix, ⚡ Faster loads", color: "#718096", fontSize: 11 } },
      { id: uid(), type: "button", content: { text: "Update now", buttonStyle: "filled", buttonColor: "#10B981", buttonTextColor: "#FFFFFF", buttonRadius: 10, action: "openUrl" } },
    ],
  },
};

// ─── Default Block Content ────────────────────────────────────────────────────

function defaultBlockContent(type: BlockType): BlockContent {
  switch (type) {
    case "heading": return { text: "Your heading here", fontSize: 18, fontWeight: "bold", color: "#1A202C", alignment: "left" };
    case "text": return { text: "Add your message text here.", fontSize: 14, fontWeight: "normal", color: "#4A5568", alignment: "left", lineHeight: 1.5 };
    case "image": return { imageUrl: "", imageAlt: "", imageHeight: 140 };
    case "button": return { text: "Click here", buttonStyle: "filled", buttonColor: "var(--brand-primary)", buttonTextColor: "#FFFFFF", buttonRadius: 8, action: "dismiss" };
    case "divider": return { dividerColor: "#E2E8F0", thickness: 1 };
    case "spacer": return { spacerHeight: 16 };
    default: return {};
  }
}

// ─── Per-surface configuration ────────────────────────────────────────────────
// One object drives every surface-specific behaviour: which blocks are allowed,
// which Style controls render, and which dismiss paths exist. This is the same
// config the SDK teams consume — BUG-03 / BUG-04 / BUG-02 are all one source.

interface SurfaceConfig {
  allowedBlocks: BlockType[];
  style: {
    cornerRadius: boolean;
    backdropOpacity: boolean;
    width: boolean;                              // exposes a width control
    solidBackgroundOnly: boolean;                // banner: no gradient/image bg
    animations: { value: string; label: string }[];
    positions: { value: string; label: string }[]; // empty = no position control
  };
  dismiss: {
    closeButton: "required" | "optional" | "none";
    tapBackdrop: boolean;
    swipe: boolean;
    autoDismiss: "default" | "optional" | "none";
  };
}

const ALL_BLOCKS: BlockType[] = ["heading", "text", "image", "button", "divider", "spacer"];

const SURFACE_CONFIG: Record<MessageType, SurfaceConfig> = {
  modal: {
    allowedBlocks: ALL_BLOCKS,
    style: {
      cornerRadius: true, backdropOpacity: true, width: true, solidBackgroundOnly: false,
      animations: [{ value: "fade", label: "Fade" }, { value: "slide-up", label: "Slide" }, { value: "pop", label: "Scale" }],
      positions: [{ value: "center", label: "Center" }, { value: "bottom", label: "Bottom" }, { value: "top", label: "Top" }],
    },
    dismiss: { closeButton: "optional", tapBackdrop: true, swipe: true, autoDismiss: "none" },
  },
  banner: {
    allowedBlocks: ["heading", "text", "image", "button"],
    style: {
      cornerRadius: false, backdropOpacity: false, width: false, solidBackgroundOnly: true,
      animations: [{ value: "slide-up", label: "Slide" }],
      positions: [{ value: "top", label: "Top" }, { value: "bottom", label: "Bottom" }],
    },
    dismiss: { closeButton: "optional", tapBackdrop: false, swipe: true, autoDismiss: "default" },
  },
  "slide-in": {
    allowedBlocks: ALL_BLOCKS,
    style: {
      cornerRadius: true, backdropOpacity: false, width: true, solidBackgroundOnly: false,
      animations: [{ value: "slide-up", label: "Slide" }],
      positions: [
        { value: "bottom-right", label: "Bottom right" }, { value: "bottom-left", label: "Bottom left" },
        { value: "top-right", label: "Top right" }, { value: "top-left", label: "Top left" },
      ],
    },
    dismiss: { closeButton: "optional", tapBackdrop: false, swipe: true, autoDismiss: "optional" },
  },
  fullscreen: {
    allowedBlocks: ALL_BLOCKS,
    style: {
      cornerRadius: false, backdropOpacity: false, width: false, solidBackgroundOnly: false,
      animations: [{ value: "fade", label: "Fade" }, { value: "slide-up", label: "Slide" }],
      positions: [],
    },
    dismiss: { closeButton: "required", tapBackdrop: false, swipe: false, autoDismiss: "none" },
  },
};

// Banner slot character limits (Part E1) — hard caps enforced in the UI.
const BANNER_LIMITS = { heading: 30, text: 35, button: 12 };

// Coerce a style object onto a surface's allowed animation/position set so a
// Chakra/native <select> never renders blank on a value it doesn't offer (ANTI-12).
function coerceStyleForSurface(style: MessageStyle, type: MessageType): MessageStyle {
  const cfg = SURFACE_CONFIG[type];
  const next = { ...style };
  if (!cfg.style.animations.some(a => a.value === next.animation)) next.animation = cfg.style.animations[0].value;
  if (cfg.style.positions.length && !cfg.style.positions.some(p => p.value === next.position)) next.position = cfg.style.positions[0].value;
  // Dismiss defaults appropriate to the surface.
  if (cfg.dismiss.closeButton === "required") next.closeButton = true;
  if (cfg.dismiss.autoDismiss === "default" && (!next.autoDismissSecs || next.autoDismissSecs <= 0)) next.autoDismissSecs = 6;
  if (cfg.dismiss.autoDismiss === "none") next.autoDismissSecs = 0;
  if (!cfg.dismiss.tapBackdrop) next.tapBackdrop = false;
  if (!cfg.dismiss.swipe) next.swipeToDismiss = false;
  return next;
}

// Does this message have at least one working exit path? (BUG-02 publish guard)
function hasExitPath(style: MessageStyle, type: MessageType): boolean {
  const cfg = SURFACE_CONFIG[type];
  if (cfg.dismiss.closeButton === "required") return style.closeButton;
  return style.closeButton
    || (cfg.dismiss.tapBackdrop && style.tapBackdrop)
    || (cfg.dismiss.swipe && style.swipeToDismiss)
    || (cfg.dismiss.autoDismiss !== "none" && style.autoDismissSecs > 0);
}

// ─── Contrast checking (BUG-08) ───────────────────────────────────────────────
// WCAG 2.1 relative-luminance contrast ratio. Handles hex and CSS custom
// properties (var(--brand-primary)) by resolving them off :root at call time.

function resolveCssColor(c?: string): string {
  if (!c) return "#000000";
  if (c.startsWith("#")) return c;
  if (c.startsWith("var(") && typeof window !== "undefined") {
    const name = c.slice(4, -1).split(",")[0].trim();
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    if (v) return v.startsWith("#") ? v : `#${v}`;
  }
  return c;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  let h = hex.replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) return null;
  return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16) };
}

function relLuminance({ r, g, b }: { r: number; g: number; b: number }): number {
  const f = (v: number) => { const s = v / 255; return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4); };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

// Returns the WCAG contrast ratio (1–21), or null if a colour can't be parsed.
function contrastRatio(fg?: string, bg?: string): number | null {
  const a = hexToRgb(resolveCssColor(fg));
  const b = hexToRgb(resolveCssColor(bg));
  if (!a || !b) return null;
  const la = relLuminance(a), lb = relLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

// A text block passes if it clears the AA threshold for its size: 3:1 for large
// text (≥18px, or ≥14px bold), else 4.5:1.
function textBlockContrastFails(block: Block, background: string): boolean {
  if (block.type !== "heading" && block.type !== "text") return false;
  if (!(block.content.text || "").trim()) return false;
  const ratio = contrastRatio(block.content.color, background);
  if (ratio == null) return false; // can't evaluate (e.g. image background) → don't block
  const size = block.content.fontSize || (block.type === "heading" ? 18 : 14);
  const large = size >= 18 || (size >= 14 && (block.content.fontWeight === "bold" || block.content.fontWeight === "semibold"));
  return ratio < (large ? 3 : 4.5);
}

// ─── BlockView ────────────────────────────────────────────────────────────────

interface BlockViewProps {
  block: Block;
  isSelected: boolean;
  onSelect: () => void;
  onUpdate: (content: Partial<BlockContent>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
}

function BlockActionBar({ onDelete, onDuplicate }: { onDelete: () => void; onDuplicate: () => void }) {
  return (
    <div
      className="absolute -top-7 right-1 flex items-center gap-1 rounded-md px-1.5 py-1 z-30 shadow-lg"
      style={{ backgroundColor: "#1A202C" }}
      onClick={(e) => e.stopPropagation()}
    >
      <GripVertical size={12} className="text-gray-400 cursor-grab" />
      <button className="text-gray-300 hover:text-white p-0.5 transition-colors" onClick={onDuplicate}><Copy size={12} /></button>
      <button className="text-gray-300 hover:text-red-400 p-0.5 transition-colors" onClick={onDelete}><Trash2 size={12} /></button>
    </div>
  );
}

function BlockView({ block, isSelected, onSelect, onUpdate, onDelete, onDuplicate }: BlockViewProps) {
  const { content } = block;

  const fontWeightClass = {
    normal: "font-normal",
    medium: "font-medium",
    semibold: "font-semibold",
    bold: "font-bold",
  }[content.fontWeight || "normal"];

  const alignClass = {
    left: "text-left",
    center: "text-center",
    right: "text-right",
  }[content.alignment || "left"];

  // Image block — edge-to-edge (no px-4 padding)
  if (block.type === "image") {
    return (
      <div
        className={`relative cursor-pointer ${isSelected ? "ring-2 ring-blue-600 ring-offset-1 rounded" : ""}`}
        onClick={(e) => { e.stopPropagation(); onSelect(); }}
      >
        {isSelected && <BlockActionBar onDelete={onDelete} onDuplicate={onDuplicate} />}
        {content.imageUrl ? (
          <img
            src={content.imageUrl}
            alt={content.imageAlt || ""}
            className="w-full object-cover"
            style={{ height: content.imageHeight || 140, display: "block" }}
          />
        ) : (
          <div
            className="w-full flex flex-col items-center justify-center gap-1"
            style={{ height: content.imageHeight || 140, backgroundColor: "#F7FAFC", border: "1px dashed #CBD5E0" }}
          >
            <ImageIcon size={22} style={{ color: "#A0AEC0" }} />
            <span className="text-xs" style={{ color: "#A0AEC0" }}>No image uploaded</span>
          </div>
        )}
      </div>
    );
  }

  let inner: React.ReactNode = null;

  const isBlank = !content.text || content.text.trim() === "";
  // BUG-07 — an empty text/heading must read as an editor placeholder, never as
  // shipped content. Dashed outline + greyed label, never rendered on publish.
  const emptyState = (label: string) => (
    <span className={`${alignClass} block rounded`} style={{
      border: "1px dashed #CBD5E0", color: "#A0AEC0", fontSize: 12, fontStyle: "italic",
      padding: "2px 6px",
    }}>{label}</span>
  );

  switch (block.type) {
    case "heading":
      inner = isBlank ? emptyState("Empty heading") : (
        <p className={`${fontWeightClass} ${alignClass} leading-tight`}
          style={{ fontSize: content.fontSize || 18, color: content.color || "#1A202C" }}>
          {content.text}
        </p>
      );
      break;

    case "text":
      inner = isBlank ? emptyState("Empty text") : (
        <p className={`${fontWeightClass} ${alignClass}`}
          style={{ fontSize: content.fontSize || 14, color: content.color || "#4A5568", lineHeight: content.lineHeight || 1.5 }}>
          {content.text}
        </p>
      );
      break;

    case "button": {
      const bs = content.buttonStyle || "filled";
      let btnStyle: React.CSSProperties = { borderRadius: content.buttonRadius ?? 8 };
      if (bs === "filled") {
        btnStyle = { ...btnStyle, backgroundColor: content.buttonColor || "var(--brand-primary)", color: content.buttonTextColor || "#FFFFFF" };
      } else if (bs === "outline") {
        btnStyle = { ...btnStyle, border: `2px solid ${content.buttonColor || "var(--brand-primary)"}`, color: content.buttonColor || "var(--brand-primary)" };
      } else {
        btnStyle = { ...btnStyle, color: content.buttonColor || "var(--brand-primary)" };
      }
      inner = (
        <button className="w-full py-2.5 text-sm font-semibold transition-opacity hover:opacity-90 cursor-pointer" style={btnStyle}>
          {content.text || "Button"}
        </button>
      );
      break;
    }

    case "divider":
      inner = <hr style={{ borderColor: content.dividerColor || "#E2E8F0", borderTopWidth: content.thickness || 1 }} />;
      break;

    case "spacer":
      inner = <div style={{ height: content.spacerHeight || 16 }} />;
      break;
  }

  return (
    <div
      className={`relative cursor-pointer px-4 py-1.5 ${isSelected ? "ring-2 ring-blue-600 ring-offset-1 rounded" : ""}`}
      onClick={(e) => { e.stopPropagation(); onSelect(); }}
    >
      {isSelected && <BlockActionBar onDelete={onDelete} onDuplicate={onDuplicate} />}
      {inner}
    </div>
  );
}

// A rendered close (X) affordance shown in the preview when the message has a
// close button configured. Position tweaks per surface via the `corner` prop.
function CloseChip({ dark = false }: { dark?: boolean }) {
  return (
    <div className="absolute top-2 right-2 z-30 rounded-full flex items-center justify-center pointer-events-none"
      style={{ width: 24, height: 24, backgroundColor: dark ? "rgba(0,0,0,0.35)" : "rgba(0,0,0,0.08)" }}>
      <X size={14} style={{ color: dark ? "#FFFFFF" : "#4A5568" }} />
    </div>
  );
}

// ─── Message Overlay ──────────────────────────────────────────────────────────

interface MessageOverlayProps {
  messageType: MessageType;
  messageStyle: MessageStyle;
  blocks: Block[];
  selectedBlockId: string | null;
  onSelectBlock: (id: string | null) => void;
  onUpdateBlock: (id: string, content: Partial<BlockContent>) => void;
  onDeleteBlock: (id: string) => void;
  onDuplicateBlock: (id: string) => void;
  dragOverIndex: number | null;
}

function MessageOverlay({
  messageType, messageStyle, blocks, selectedBlockId,
  onSelectBlock, onUpdateBlock, onDeleteBlock, onDuplicateBlock, dragOverIndex,
}: MessageOverlayProps) {
  const firstImage = blocks.find(b => b.type === "image");
  const firstHeading = blocks.find(b => b.type === "heading");
  const firstText = blocks.find(b => b.type === "text");
  const firstButton = blocks.find(b => b.type === "button");

  const renderBlocks = () => (
    <div className="flex flex-col" onClick={(e) => e.stopPropagation()}>
      {blocks.map((block, idx) => (
        <div key={block.id}>
          {dragOverIndex === idx && <div className="h-1 mx-4 my-1 rounded-full" style={{ backgroundColor: "var(--brand-primary)" }} />}
          <BlockView
            block={block}
            isSelected={selectedBlockId === block.id}
            onSelect={() => onSelectBlock(block.id)}
            onUpdate={(c) => onUpdateBlock(block.id, c)}
            onDelete={() => onDeleteBlock(block.id)}
            onDuplicate={() => onDuplicateBlock(block.id)}
          />
        </div>
      ))}
      {dragOverIndex === blocks.length && <div className="h-1 mx-4 my-1 rounded-full" style={{ backgroundColor: "var(--brand-primary)" }} />}
    </div>
  );

  if (blocks.length === 0) {
    return (
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="text-center p-4">
          <div className="text-3xl mb-2" style={{ color: "#CBD5E0" }}>+</div>
          <div className="text-xs" style={{ color: "#A0AEC0" }}>Drag blocks here</div>
        </div>
      </div>
    );
  }

  switch (messageType) {
    case "modal":
      return (
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{ backgroundColor: `rgba(0,0,0,${messageStyle.backdropOpacity / 100})` }}
          onClick={() => onSelectBlock(null)}
        >
          <div
            className="relative w-[90%] overflow-y-auto shadow-2xl"
            style={{ backgroundColor: messageStyle.background, borderRadius: messageStyle.cornerRadius, maxHeight: "85%" }}
            onClick={(e) => e.stopPropagation()}
          >
            {messageStyle.closeButton && <CloseChip />}
            {renderBlocks()}
            <div className="h-2" />
          </div>
        </div>
      );

    case "banner": {
      // BUG-05 / Part E1 — a banner is a fixed-slot horizontal bar. Slots are hard
      // char-capped and the action button width is capped so title/body always take
      // the remainder (they can never be squeezed to "Welc…").
      const headingText = (firstHeading?.content.text || "").slice(0, BANNER_LIMITS.heading);
      const bodyText = (firstText?.content.text || "").slice(0, BANNER_LIMITS.text);
      const btnLabel = firstButton ? (firstButton.content.text || "").slice(0, BANNER_LIMITS.button) : "";
      const isTop = messageStyle.position === "top";
      return (
        <div
          className={`absolute ${isTop ? "top-0" : "bottom-0"} left-0 right-0 flex items-center px-3 gap-2 shadow-lg`}
          style={{ height: bodyText ? 80 : 64, backgroundColor: messageStyle.background || "#FFFFFF" }}
          onClick={(e) => e.stopPropagation()}
        >
          {firstImage?.content.imageUrl ? (
            <img src={firstImage.content.imageUrl} alt={firstImage.content.imageAlt || ""} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
          ) : firstImage ? (
            <div className="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: "var(--brand-tint)" }}>
              <ImageIcon size={16} style={{ color: "#93C5FD" }} />
            </div>
          ) : null}
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold truncate" style={{ color: "#1A202C" }}>{headingText || "Banner title"}</div>
            {bodyText && <div className="text-xs truncate" style={{ color: "#718096" }}>{bodyText}</div>}
          </div>
          {btnLabel && (
            <button className="flex-shrink-0 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-white truncate"
              style={{ backgroundColor: firstButton?.content.buttonColor || "var(--brand-primary)", maxWidth: 96 }}>
              {btnLabel}
            </button>
          )}
          {messageStyle.closeButton && (
            <div className="flex-shrink-0 rounded-full flex items-center justify-center" style={{ width: 20, height: 20, backgroundColor: "rgba(0,0,0,0.06)" }}>
              <X size={12} style={{ color: "#718096" }} />
            </div>
          )}
        </div>
      );
    }

    case "slide-in":
      return (
        <div className="absolute bottom-4 right-3 w-44 rounded-2xl shadow-2xl overflow-hidden"
          style={{ backgroundColor: messageStyle.background || "#FFFFFF", borderRadius: messageStyle.cornerRadius ?? 16 }}
          onClick={(e) => e.stopPropagation()}>
          {messageStyle.closeButton && <CloseChip />}
          {firstImage?.content.imageUrl && (
            <img src={firstImage.content.imageUrl} alt={firstImage.content.imageAlt || ""} className="w-full object-cover" style={{ height: 56 }} />
          )}
          <div className="p-2.5">
            {firstHeading && <p className="text-xs font-bold leading-tight mb-1" style={{ color: "#1A202C" }}>{firstHeading.content.text}</p>}
            {firstText && <p className="text-xs mb-2 leading-tight line-clamp-2" style={{ color: "#718096" }}>{firstText.content.text}</p>}
            {firstButton && (
              <button className="w-full py-1.5 rounded-lg text-xs font-semibold text-white"
                style={{ backgroundColor: firstButton.content.buttonColor || "var(--brand-primary)", borderRadius: firstButton.content.buttonRadius ?? 8 }}>
                {firstButton.content.text}
              </button>
            )}
          </div>
        </div>
      );

    case "fullscreen": {
      const bgImage = firstImage?.content.imageUrl;
      return (
        <div className="absolute inset-0 flex flex-col justify-center overflow-y-auto"
          style={{
            paddingTop: 56,
            paddingBottom: 40,
            // BUG-09 — the canvas must reflect the chosen Background, not a hard-coded
            // gradient. A background image still wins when one is present.
            backgroundColor: messageStyle.background || "#FFFFFF",
            ...(bgImage ? { backgroundImage: `url(${bgImage})`, backgroundSize: "cover", backgroundPosition: "center" } : {}),
          }}
          onClick={() => onSelectBlock(null)}>
          {messageStyle.closeButton && <CloseChip />}
          {renderBlocks()}
        </div>
      );
    }

    default:
      return null;
  }
}

// ─── Sample App Background ────────────────────────────────────────────────────

function SampleAppBackground() {
  return (
    <div className="absolute inset-0 opacity-40">
      <div className="flex items-center justify-between px-4 pt-10 pb-3" style={{ backgroundColor: "#FFFFFF" }}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: "var(--brand-primary)" }}>
            <span className="text-white text-xs font-bold">A</span>
          </div>
          <span className="text-sm font-bold" style={{ color: "#1A202C" }}>Apex Bank</span>
        </div>
        <div className="w-8 h-8 rounded-full" style={{ background: "linear-gradient(135deg, #667eea, #764ba2)" }} />
      </div>
      <div className="mx-4 mb-3 rounded-2xl p-4" style={{ background: "linear-gradient(135deg, var(--brand-primary), #5A67D8)" }}>
        <p className="text-xs mb-1" style={{ color: "#BEE3F8" }}>Total Balance</p>
        <p className="text-xl font-bold text-white">$24,891.50</p>
        <p className="text-xs mt-1" style={{ color: "#90CDF4" }}>↑ +$312.40 this month</p>
      </div>
      <div className="flex justify-around px-4 mb-4">
        {["Send", "Receive", "Pay", "More"].map((a) => (
          <div key={a} className="flex flex-col items-center gap-1">
            <div className="w-10 h-10 rounded-full" style={{ backgroundColor: "#EBF8FF", border: "1px solid #BEE3F8" }} />
            <span className="text-xs" style={{ color: "#718096" }}>{a}</span>
          </div>
        ))}
      </div>
      <div className="px-4">
        <p className="text-xs font-semibold mb-2" style={{ color: "#A0AEC0" }}>Recent Transactions</p>
        {["-$48.20", "-$15.99", "+$3,200"].map((amt, i) => (
          <div key={i} className="flex items-center gap-3 py-2 border-b" style={{ borderColor: "#F7FAFC" }}>
            <div className="w-8 h-8 rounded-full" style={{ backgroundColor: i === 2 ? "#EBF8FF" : i === 1 ? "#FFF5F5" : "#F0FFF4" }} />
            <div className="flex-1">
              <div className="w-24 h-2.5 rounded" style={{ backgroundColor: "#E2E8F0" }} />
              <div className="w-16 h-2 rounded mt-1" style={{ backgroundColor: "#EDF2F7" }} />
            </div>
            <span className="text-xs" style={{ color: "#A0AEC0" }}>{amt}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Device Frames ────────────────────────────────────────────────────────────

function IPhoneFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative" style={{ width: 280, height: 560 }}>
      <div className="absolute inset-0 rounded-[42px] shadow-2xl" style={{ backgroundColor: "#1A1A1A", padding: 3 }}>
        <div className="absolute inset-[3px] rounded-[40px] overflow-hidden" style={{ backgroundColor: "#000" }}>
          <div className="absolute inset-[2px] rounded-[38px] overflow-hidden" style={{ backgroundColor: "#FFFFFF" }}>
            {children}
          </div>
          {/* Status bar */}
          <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-5 pt-2 z-30 pointer-events-none" style={{ height: 36 }}>
            <span className="text-white font-semibold" style={{ fontSize: 10 }}>9:41</span>
            <div className="absolute left-1/2 -translate-x-1/2 w-20 h-5 rounded-full" style={{ backgroundColor: "#000" }} />
            <div className="flex items-center gap-1">
              <div className="flex gap-0.5 items-end">
                {[3, 5, 7, 9].map((h, i) => (
                  <div key={i} className="w-0.5 rounded-sm" style={{ height: h, backgroundColor: "#FFFFFF", opacity: i < 3 ? 1 : 0.4 }} />
                ))}
              </div>
              <div className="relative" style={{ width: 20, height: 10 }}>
                <div className="absolute inset-0 rounded-sm border" style={{ borderColor: "rgba(255,255,255,0.6)" }} />
                <div className="absolute rounded-sm" style={{ left: 2, top: 2, bottom: 2, right: 4, backgroundColor: "#FFFFFF" }} />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Side buttons */}
      <div className="absolute rounded-r-sm" style={{ right: -4, top: 96, width: 4, height: 32, backgroundColor: "#555" }} />
      <div className="absolute rounded-r-sm" style={{ right: -4, top: 144, width: 4, height: 48, backgroundColor: "#555" }} />
      <div className="absolute rounded-l-sm" style={{ left: -4, top: 112, width: 4, height: 24, backgroundColor: "#555" }} />
      <div className="absolute rounded-l-sm" style={{ left: -4, top: 152, width: 4, height: 40, backgroundColor: "#555" }} />
      <div className="absolute rounded-l-sm" style={{ left: -4, top: 208, width: 4, height: 40, backgroundColor: "#555" }} />
    </div>
  );
}

function AndroidFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative" style={{ width: 272, height: 556 }}>
      <div className="absolute inset-0 rounded-[36px] shadow-2xl" style={{ backgroundColor: "#2D2D2D", padding: 3 }}>
        <div className="absolute inset-[3px] rounded-[34px] overflow-hidden" style={{ backgroundColor: "#000" }}>
          <div className="absolute inset-[2px] rounded-[32px] overflow-hidden" style={{ backgroundColor: "#FFFFFF" }}>
            {children}
          </div>
          {/* Camera pill */}
          <div className="absolute left-1/2 -translate-x-1/2 rounded-full z-30" style={{ top: 8, width: 64, height: 16, backgroundColor: "#000" }} />
          {/* Status bar */}
          <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-5 z-30 pointer-events-none" style={{ height: 32, paddingTop: 6 }}>
            <span className="text-white font-medium" style={{ fontSize: 10 }}>9:41</span>
            <div className="relative" style={{ width: 20, height: 10 }}>
              <div className="absolute inset-0 rounded-sm border" style={{ borderColor: "rgba(255,255,255,0.6)" }} />
              <div className="absolute rounded-sm" style={{ left: 2, top: 2, bottom: 2, width: "80%", backgroundColor: "#FFFFFF" }} />
            </div>
          </div>
          {/* Nav bar */}
          <div className="absolute bottom-0 left-0 right-0 flex justify-around items-center" style={{ height: 28, backgroundColor: "#111" }}>
            {["◁", "●", "▢"].map((s) => (
              <span key={s} className="text-white" style={{ opacity: 0.6, fontSize: 10 }}>{s}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Color Swatches ───────────────────────────────────────────────────────────

function ColorSwatches({ colors, value, onChange }: { colors: string[]; value: string; onChange: (c: string) => void }) {
  return (
    <div className="flex flex-wrap gap-1.5 mb-2">
      {colors.map((c) => (
        <button key={c} className="w-6 h-6 rounded-md border-2 transition-transform hover:scale-110"
          style={{
            backgroundColor: c,
            borderColor: value === c ? "var(--brand-primary)" : "transparent",
            outline: c === "#FFFFFF" ? "1px solid #E2E8F0" : undefined,
          }}
          onClick={() => onChange(c)}
        />
      ))}
      <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)}
        className="w-6 h-6 rounded-md cursor-pointer border p-0" style={{ borderColor: "#E2E8F0" }} title="Custom color" />
    </div>
  );
}

function PanelLabel({ children }: { children: React.ReactNode }) {
  return <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: "#718096" }}>{children}</label>;
}

function PanelSection({ children, title }: { children: React.ReactNode; title?: string }) {
  return (
    <div className="mb-4">
      {title && <PanelLabel>{title}</PanelLabel>}
      {children}
    </div>
  );
}

// ─── Block Property Panel ─────────────────────────────────────────────────────

function BlockPropertyPanel({ block, onUpdate, background }: { block: Block; onUpdate: (c: Partial<BlockContent>) => void; background?: string | null }) {
  const { content } = block;
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [imgError, setImgError] = useState<string | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validationError = validateImageFile(file);
    if (validationError) {
      setImgError(validationError);
      e.target.value = "";
      return;
    }
    setUploading(true);
    setImgError(null);
    try {
      const url = await uploadMediaFile(file);
      onUpdate({ imageUrl: url });
    } catch (err) {
      setImgError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  switch (block.type) {
    case "heading":
    case "text":
      return (
        <div>
          <PanelSection title="Content">
            <textarea className="w-full text-sm border rounded-lg p-2.5 resize-none focus:outline-none focus:ring-2"
              style={{ borderColor: "#E2E8F0", outlineColor: "var(--brand-primary)" }}
              rows={3} value={content.text || ""} onChange={(e) => onUpdate({ text: e.target.value })} />
          </PanelSection>
          <PanelSection title="Font Size">
            <div className="flex items-center gap-2">
              <input type="range" min={8} max={32} value={content.fontSize || 14}
                onChange={(e) => onUpdate({ fontSize: Number(e.target.value) })} className="flex-1" />
              <span className="text-xs w-8 text-right" style={{ color: "#718096" }}>{content.fontSize || 14}px</span>
            </div>
          </PanelSection>
          <PanelSection title="Font Weight">
            <select className="w-full text-sm border rounded-lg p-2 focus:outline-none"
              style={{ borderColor: "#E2E8F0" }}
              value={content.fontWeight || "normal"} onChange={(e) => onUpdate({ fontWeight: e.target.value as BlockContent["fontWeight"] })}>
              {["normal", "medium", "semibold", "bold"].map(w => (
                <option key={w} value={w}>{w.charAt(0).toUpperCase() + w.slice(1)}</option>
              ))}
            </select>
          </PanelSection>
          <PanelSection title="Color">
            <ColorSwatches colors={TEXT_COLORS} value={content.color || "#1A202C"} onChange={(c) => onUpdate({ color: c })} />
            {background && textBlockContrastFails(block, background) && (
              <p className="text-xs mt-1" style={{ color: "#D69E2E" }}>
                ⚠ Low contrast against the background — may be hard to read.
              </p>
            )}
          </PanelSection>
          <PanelSection title="Alignment">
            <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: "#E2E8F0" }}>
              {(["left", "center", "right"] as const).map((a) => (
                <button key={a} className="flex-1 py-2 flex justify-center items-center transition-colors"
                  style={{ backgroundColor: content.alignment === a ? "var(--brand-primary)" : "#FFFFFF" }}
                  onClick={() => onUpdate({ alignment: a })}>
                  {a === "left" ? <AlignLeft size={14} color={content.alignment === a ? "#fff" : "#718096"} /> :
                   a === "center" ? <AlignCenter size={14} color={content.alignment === a ? "#fff" : "#718096"} /> :
                   <AlignRight size={14} color={content.alignment === a ? "#fff" : "#718096"} />}
                </button>
              ))}
            </div>
          </PanelSection>
          <PanelSection title="Line Height">
            <select className="w-full text-sm border rounded-lg p-2 focus:outline-none"
              style={{ borderColor: "#E2E8F0" }}
              value={content.lineHeight || 1.5} onChange={(e) => onUpdate({ lineHeight: Number(e.target.value) })}>
              {[1, 1.25, 1.5, 1.75, 2].map(lh => <option key={lh} value={lh}>{lh}</option>)}
            </select>
          </PanelSection>
        </div>
      );

    case "image":
      return (
        <div>
          <PanelSection title="Image">
            <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFileChange} />
            <button type="button"
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border text-sm font-medium transition-colors disabled:opacity-60"
              style={{ borderColor: "#E2E8F0", color: "#4A5568" }}
              disabled={uploading}
              onClick={() => fileRef.current?.click()}>
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {uploading ? "Uploading…" : content.imageUrl ? "Replace image" : "Upload image"}
            </button>
            {imgError && <p className="text-xs mt-1.5" style={{ color: "#E53E3E" }}>{imgError}</p>}
            {content.imageUrl && (
              <div className="mt-2.5 rounded-lg overflow-hidden border" style={{ borderColor: "#E2E8F0" }}>
                <img src={content.imageUrl} alt={content.imageAlt || ""} className="w-full object-cover" style={{ height: 80 }} />
              </div>
            )}
          </PanelSection>
          <PanelSection title="Alt Text">
            <input type="text" className="w-full text-sm border rounded-lg p-2.5 focus:outline-none"
              style={{ borderColor: "#E2E8F0" }}
              value={content.imageAlt || ""} onChange={(e) => onUpdate({ imageAlt: e.target.value })} placeholder="Describe this image..." />
          </PanelSection>
          <PanelSection title="Height">
            <div className="flex items-center gap-2">
              <input type="range" min={60} max={280} value={content.imageHeight || 140}
                onChange={(e) => onUpdate({ imageHeight: Number(e.target.value) })} className="flex-1" />
              <span className="text-xs w-10 text-right" style={{ color: "#718096" }}>{content.imageHeight || 140}px</span>
            </div>
          </PanelSection>
        </div>
      );

    case "button":
      return (
        <div>
          <PanelSection title="Label">
            <input type="text" className="w-full text-sm border rounded-lg p-2.5 focus:outline-none"
              style={{ borderColor: "#E2E8F0" }}
              value={content.text || ""} onChange={(e) => onUpdate({ text: e.target.value })} />
          </PanelSection>
          <PanelSection title="Style">
            <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: "#E2E8F0" }}>
              {(["filled", "outline", "ghost"] as const).map((s) => (
                <button key={s} className="flex-1 py-1.5 text-xs font-medium transition-colors"
                  style={{ backgroundColor: content.buttonStyle === s ? "var(--brand-primary)" : "#FFFFFF", color: content.buttonStyle === s ? "#fff" : "#718096" }}
                  onClick={() => onUpdate({ buttonStyle: s })}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          </PanelSection>
          <PanelSection title="Color">
            <ColorSwatches colors={ACCENT_COLORS} value={content.buttonColor || "var(--brand-primary)"} onChange={(c) => onUpdate({ buttonColor: c })} />
          </PanelSection>
          <PanelSection title="Corner Radius">
            <div className="flex items-center gap-2">
              <input type="range" min={0} max={24} value={content.buttonRadius ?? 8}
                onChange={(e) => onUpdate({ buttonRadius: Number(e.target.value) })} className="flex-1" />
              <span className="text-xs w-8 text-right" style={{ color: "#718096" }}>{content.buttonRadius ?? 8}px</span>
            </div>
          </PanelSection>
          <PanelSection title="Action">
            <select className="w-full text-sm border rounded-lg p-2 focus:outline-none" style={{ borderColor: "#E2E8F0" }}
              value={content.action || "dismiss"} onChange={(e) => onUpdate({ action: e.target.value as BlockContent["action"] })}>
              <option value="openUrl">Open URL</option>
              <option value="deepLink">Deep Link</option>
              <option value="dismiss">Dismiss</option>
            </select>
          </PanelSection>
          {(content.action === "openUrl" || content.action === "deepLink") && (
            <PanelSection title="URL">
              <input type="text" className="w-full text-sm border rounded-lg p-2.5 focus:outline-none"
                style={{ borderColor: !content.actionUrl?.trim() ? "#FEB2B2" : "#E2E8F0" }}
                value={content.actionUrl || ""} onChange={(e) => onUpdate({ actionUrl: e.target.value })}
                placeholder={content.action === "openUrl" ? "https://..." : "myapp://..."} />
              {!content.actionUrl?.trim() && <p className="text-xs mt-1" style={{ color: "#E53E3E" }}>A URL is required for this action.</p>}
            </PanelSection>
          )}
          {content.action === "deepLink" && (
            <PanelSection title="Fallback URL">
              <input type="text" className="w-full text-sm border rounded-lg p-2.5 focus:outline-none"
                style={{ borderColor: !content.actionFallbackUrl?.trim() ? "#FEB2B2" : "#E2E8F0" }}
                value={content.actionFallbackUrl || ""} onChange={(e) => onUpdate({ actionFallbackUrl: e.target.value })}
                placeholder="https://… (opened if the deep link can't resolve)" />
              {!content.actionFallbackUrl?.trim()
                ? <p className="text-xs mt-1" style={{ color: "#E53E3E" }}>A fallback is required — without it a failed route leaves a dead button.</p>
                : null}
            </PanelSection>
          )}
        </div>
      );

    case "divider":
      return (
        <div>
          <PanelSection title="Color">
            <ColorSwatches colors={["#E2E8F0", "#CBD5E0", "#A0AEC0", "#718096", "#1A202C"]}
              value={content.dividerColor || "#E2E8F0"} onChange={(c) => onUpdate({ dividerColor: c })} />
          </PanelSection>
          <PanelSection title="Thickness">
            <div className="flex items-center gap-2">
              <input type="range" min={1} max={4} value={content.thickness || 1}
                onChange={(e) => onUpdate({ thickness: Number(e.target.value) })} className="flex-1" />
              <span className="text-xs w-6 text-right" style={{ color: "#718096" }}>{content.thickness || 1}px</span>
            </div>
          </PanelSection>
        </div>
      );

    case "spacer":
      return (
        <div>
          <PanelSection title="Height">
            <div className="flex items-center gap-2">
              <input type="range" min={4} max={64} value={content.spacerHeight || 16}
                onChange={(e) => onUpdate({ spacerHeight: Number(e.target.value) })} className="flex-1" />
              <span className="text-xs w-8 text-right" style={{ color: "#718096" }}>{content.spacerHeight || 16}px</span>
            </div>
          </PanelSection>
        </div>
      );

    default:
      return <div className="text-sm" style={{ color: "#A0AEC0" }}>No properties available.</div>;
  }
}

// ─── Message Settings Panel ───────────────────────────────────────────────────

type PropTab = "style" | "trigger" | "audience" | "frequency" | "goal";

function MessageSettingsPanel({
  propTab, setPropTab, messageType, messageStyle, setMessageStyle,
  triggerSettings, setTriggerSettings,
  audienceSettings, setAudienceSettings,
  frequencySettings, setFrequencySettings,
  goalSettings, setGoalSettings,
}: {
  propTab: PropTab;
  setPropTab: (t: PropTab) => void;
  messageType: MessageType;
  messageStyle: MessageStyle;
  setMessageStyle: (s: MessageStyle) => void;
  triggerSettings: TriggerSettings;
  setTriggerSettings: (s: TriggerSettings) => void;
  audienceSettings: AudienceSettings;
  setAudienceSettings: (s: AudienceSettings) => void;
  frequencySettings: FrequencySettings;
  setFrequencySettings: (s: FrequencySettings) => void;
  goalSettings: GoalSettings;
  setGoalSettings: (s: GoalSettings) => void;
}) {
  const TABS: { id: PropTab; label: string; icon: React.ReactNode }[] = [
    { id: "style", label: "Style", icon: <Layers size={13} /> },
    { id: "trigger", label: "Trigger", icon: <Clock size={13} /> },
    { id: "audience", label: "Audience", icon: <Users size={13} /> },
    { id: "frequency", label: "Frequency", icon: <Settings size={13} /> },
    { id: "goal", label: "Goal", icon: <Target size={13} /> },
  ];

  const [cohorts, setCohorts] = useState<{ id: string; name: string; userCount: number }[]>([]);
  const [cohortsLoading, setCohortsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setCohortsLoading(true);
      try {
        const { data: res } = await axios.get(`${process.env.REACT_APP_API_URL}/cohorts`, { headers: getHeaders() });
        const list = (res?.data?.cohorts || []).map((c: any) => ({ id: c._id, name: c.name, userCount: c.user_count || 0 }));
        if (!cancelled) setCohorts(list);
      } catch {
        if (!cancelled) setCohorts([]);
      } finally {
        if (!cancelled) setCohortsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div>
      {/* Tabs */}
      <div className="flex flex-wrap gap-1 mb-4 p-1 rounded-lg" style={{ backgroundColor: "#F7FAFC", border: "1px solid #E2E8F0" }}>
        {TABS.map((tab) => (
          <button key={tab.id}
            className="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all"
            style={{
              backgroundColor: propTab === tab.id ? "#FFFFFF" : "transparent",
              color: propTab === tab.id ? "#1A202C" : "#718096",
              boxShadow: propTab === tab.id ? "0 1px 3px rgba(0,0,0,0.1)" : undefined,
            }}
            onClick={() => setPropTab(tab.id)}>
            <span style={{ color: propTab === tab.id ? "var(--brand-primary)" : "#A0AEC0" }}>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {propTab === "style" && (() => {
        // BUG-03 — every Style control is gated by the surface's config, so a
        // meaningless control (corner radius on a full-bleed fullscreen, backdrop
        // on an edge banner) simply isn't shown.
        const cfg = SURFACE_CONFIG[messageType];
        const set = (patch: Partial<MessageStyle>) => setMessageStyle({ ...messageStyle, ...patch });
        return (
          <div>
            <PanelSection title="Background">
              <ColorSwatches colors={BG_COLORS} value={messageStyle.background} onChange={(c) => set({ background: c })} />
              {cfg.style.solidBackgroundOnly && <p className="text-xs" style={{ color: "#A0AEC0" }}>Banners use a solid background only.</p>}
            </PanelSection>

            {cfg.style.cornerRadius && (
              <PanelSection title="Corner Radius">
                <div className="flex items-center gap-2">
                  <input type="range" min={0} max={32} value={messageStyle.cornerRadius}
                    onChange={(e) => set({ cornerRadius: Number(e.target.value) })} className="flex-1" />
                  <span className="text-xs w-8 text-right" style={{ color: "#718096" }}>{messageStyle.cornerRadius}px</span>
                </div>
              </PanelSection>
            )}

            {cfg.style.backdropOpacity && (
              <PanelSection title="Backdrop Opacity">
                <div className="flex items-center gap-2">
                  <input type="range" min={0} max={80} value={messageStyle.backdropOpacity}
                    onChange={(e) => set({ backdropOpacity: Number(e.target.value) })} className="flex-1" />
                  <span className="text-xs w-8 text-right" style={{ color: "#718096" }}>{messageStyle.backdropOpacity}%</span>
                </div>
              </PanelSection>
            )}

            <PanelSection title="Animation">
              <select className="w-full text-sm border rounded-lg p-2 focus:outline-none" style={{ borderColor: "#E2E8F0" }}
                value={messageStyle.animation} onChange={(e) => set({ animation: e.target.value })}>
                {cfg.style.animations.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </PanelSection>

            {cfg.style.positions.length > 0 && (
              <PanelSection title="Position">
                <select className="w-full text-sm border rounded-lg p-2 focus:outline-none" style={{ borderColor: "#E2E8F0" }}
                  value={messageStyle.position} onChange={(e) => set({ position: e.target.value })}>
                  {cfg.style.positions.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </PanelSection>
            )}

            {/* Dismiss group (BUG-02) — driven by the same surface config. */}
            <div className="mt-2 pt-3 border-t" style={{ borderColor: "#E2E8F0" }}>
              <PanelLabel>Dismiss</PanelLabel>
              {cfg.dismiss.closeButton !== "none" && (
                <label className="flex items-center justify-between py-1.5 cursor-pointer">
                  <span className="text-sm" style={{ color: "#4A5568" }}>
                    Close button {cfg.dismiss.closeButton === "required" && <span style={{ color: "#E53E3E" }}>*</span>}
                  </span>
                  <input type="checkbox" checked={messageStyle.closeButton}
                    disabled={cfg.dismiss.closeButton === "required"}
                    onChange={(e) => set({ closeButton: e.target.checked })} />
                </label>
              )}
              {cfg.dismiss.tapBackdrop && (
                <label className="flex items-center justify-between py-1.5 cursor-pointer">
                  <span className="text-sm" style={{ color: "#4A5568" }}>Tap backdrop to close</span>
                  <input type="checkbox" checked={messageStyle.tapBackdrop} onChange={(e) => set({ tapBackdrop: e.target.checked })} />
                </label>
              )}
              {cfg.dismiss.swipe && (
                <label className="flex items-center justify-between py-1.5 cursor-pointer">
                  <span className="text-sm" style={{ color: "#4A5568" }}>Swipe to dismiss</span>
                  <input type="checkbox" checked={messageStyle.swipeToDismiss} onChange={(e) => set({ swipeToDismiss: e.target.checked })} />
                </label>
              )}
              {cfg.dismiss.autoDismiss !== "none" && (
                <PanelSection title="Auto-dismiss (seconds)">
                  <div className="flex items-center gap-2">
                    <input type="range" min={0} max={30} value={messageStyle.autoDismissSecs}
                      onChange={(e) => set({ autoDismissSecs: Number(e.target.value) })} className="flex-1" />
                    <span className="text-xs w-14 text-right" style={{ color: "#718096" }}>
                      {messageStyle.autoDismissSecs > 0 ? `${messageStyle.autoDismissSecs}s` : "Off"}
                    </span>
                  </div>
                </PanelSection>
              )}
              {!hasExitPath(messageStyle, messageType) && (
                <p className="text-xs mt-1" style={{ color: "#E53E3E" }}>
                  ⚠ This message has no way to close — add a close button, swipe, or auto-dismiss.
                </p>
              )}
            </div>
          </div>
        );
      })()}

      {propTab === "trigger" && (
        <div>
          <PanelSection title="Show when">
            <select className="w-full text-sm border rounded-lg p-2 focus:outline-none" style={{ borderColor: "#E2E8F0" }}
              value={triggerSettings.event} onChange={(e) => setTriggerSettings({ ...triggerSettings, event: e.target.value })}>
              <option value="app_open">App open</option>
              <option value="screen_view">Screen view</option>
              <option value="session_start">Session start</option>
              <option value="custom_event">Custom event</option>
            </select>
          </PanelSection>
          <PanelSection title="Delay">
            <div className="flex gap-2">
              <input type="number" min={0} className="flex-1 text-sm border rounded-lg p-2.5 focus:outline-none"
                style={{ borderColor: "#E2E8F0" }}
                value={triggerSettings.delay} onChange={(e) => setTriggerSettings({ ...triggerSettings, delay: Number(e.target.value) })} />
              <select className="text-sm border rounded-lg p-2 focus:outline-none" style={{ borderColor: "#E2E8F0" }}
                value={triggerSettings.delayUnit} onChange={(e) => setTriggerSettings({ ...triggerSettings, delayUnit: e.target.value })}>
                <option value="seconds">Seconds</option>
                <option value="minutes">Minutes</option>
              </select>
            </div>
          </PanelSection>
        </div>
      )}

      {propTab === "audience" && (
        <div>
          <PanelSection title="Send to">
            <div className="space-y-2">
              {(["all", "cohort"] as const).map((type) => (
                <label key={type} className="flex items-center gap-2.5 cursor-pointer"
                  onClick={() => setAudienceSettings(
                    type === "all"
                      ? { type: "all" }
                      : { type: "cohort", cohortId: audienceSettings.cohortId, cohortName: audienceSettings.cohortName, estimatedReach: audienceSettings.estimatedReach }
                  )}>
                  <div className="w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                    style={{ borderColor: audienceSettings.type === type ? "var(--brand-primary)" : "#CBD5E0" }}>
                    {audienceSettings.type === type && <div className="w-2 h-2 rounded-full" style={{ backgroundColor: "var(--brand-primary)" }} />}
                  </div>
                  <span className="text-sm" style={{ color: "#4A5568" }}>
                    {type === "all" ? "All users" : "Specific cohort"}
                  </span>
                </label>
              ))}
            </div>
          </PanelSection>

          {audienceSettings.type === "cohort" && (
            <PanelSection title="Cohort">
              <select className="w-full text-sm border rounded-lg p-2 focus:outline-none" style={{ borderColor: "#E2E8F0" }}
                value={audienceSettings.cohortId || ""}
                disabled={cohortsLoading}
                onChange={(e) => {
                  const c = cohorts.find(x => x.id === e.target.value);
                  setAudienceSettings({ ...audienceSettings, cohortId: c?.id, cohortName: c?.name, estimatedReach: c?.userCount });
                }}>
                <option value="">{cohortsLoading ? "Loading cohorts…" : "Select a cohort"}</option>
                {cohorts.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} ({c.userCount.toLocaleString()})</option>
                ))}
              </select>
              {!cohortsLoading && cohorts.length === 0 && (
                <p className="text-xs mt-1.5" style={{ color: "#A0AEC0" }}>No active cohorts found.</p>
              )}
            </PanelSection>
          )}

          <div className="rounded-xl p-3 border" style={{ backgroundColor: "var(--brand-tint)", borderColor: "var(--brand-border)" }}>
            <p className="text-xs font-semibold mb-0.5" style={{ color: "var(--brand-primary-hover)" }}>Estimated reach</p>
            <p className="text-xl font-bold" style={{ color: "#1E3A8A" }}>
              {audienceSettings.type === "all" ? "All users" : (audienceSettings.estimatedReach ?? 0).toLocaleString()}
            </p>
            <p className="text-xs mt-0.5" style={{ color: "#60A5FA" }}>
              {audienceSettings.type === "all" ? "Sent to your entire audience" : "users in this cohort"}
            </p>
          </div>
        </div>
      )}

      {propTab === "frequency" && (
        <div>
          <PanelSection title="Show frequency">
            <div className="space-y-2">
              {(["once", "session", "max"] as const).map((type) => (
                <label key={type} className="flex items-center gap-2.5 cursor-pointer"
                  onClick={() => setFrequencySettings({ ...frequencySettings, type })}>
                  <div className="w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                    style={{ borderColor: frequencySettings.type === type ? "var(--brand-primary)" : "#CBD5E0" }}>
                    {frequencySettings.type === type && <div className="w-2 h-2 rounded-full" style={{ backgroundColor: "var(--brand-primary)" }} />}
                  </div>
                  <span className="text-sm" style={{ color: "#4A5568" }}>
                    {type === "once" ? "Once per user" : type === "session" ? "Every session" : "Max N times"}
                  </span>
                </label>
              ))}
            </div>
          </PanelSection>
          {frequencySettings.type === "max" && (
            <PanelSection title="Max times">
              <input type="number" min={1} className="w-full text-sm border rounded-lg p-2.5 focus:outline-none"
                style={{ borderColor: "#E2E8F0" }}
                value={frequencySettings.maxTimes || 3}
                onChange={(e) => setFrequencySettings({ ...frequencySettings, maxTimes: Number(e.target.value) })} />
            </PanelSection>
          )}
          <PanelSection title="Date Range">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <PanelLabel>Start</PanelLabel>
                <input type="date" className="w-full text-xs border rounded-lg p-2 focus:outline-none"
                  style={{ borderColor: "#E2E8F0" }}
                  value={frequencySettings.startDate}
                  onChange={(e) => setFrequencySettings({ ...frequencySettings, startDate: e.target.value })} />
              </div>
              <div>
                <PanelLabel>End</PanelLabel>
                <input type="date" className="w-full text-xs border rounded-lg p-2 focus:outline-none"
                  style={{ borderColor: "#E2E8F0" }}
                  value={frequencySettings.endDate}
                  onChange={(e) => setFrequencySettings({ ...frequencySettings, endDate: e.target.value })} />
              </div>
            </div>
          </PanelSection>
        </div>
      )}

      {propTab === "goal" && (
        <div>
          <PanelSection title="Conversion Event">
            <select className="w-full text-sm border rounded-lg p-2 focus:outline-none" style={{ borderColor: "#E2E8F0" }}
              value={goalSettings.event} onChange={(e) => setGoalSettings({ ...goalSettings, event: e.target.value })}>
              <option value="purchase">Purchase</option>
              <option value="signup">Sign up</option>
              <option value="upgrade">Upgrade</option>
              <option value="feature_used">Feature used</option>
              <option value="session_start">Session start</option>
            </select>
          </PanelSection>
          <PanelSection title="Conversion Window">
            <div className="flex gap-2">
              <input type="number" min={1} className="flex-1 text-sm border rounded-lg p-2.5 focus:outline-none"
                style={{ borderColor: "#E2E8F0" }}
                value={goalSettings.window} onChange={(e) => setGoalSettings({ ...goalSettings, window: Number(e.target.value) })} />
              <select className="text-sm border rounded-lg p-2 focus:outline-none" style={{ borderColor: "#E2E8F0" }}
                value={goalSettings.windowUnit} onChange={(e) => setGoalSettings({ ...goalSettings, windowUnit: e.target.value as "hours" | "days" })}>
                <option value="hours">Hours</option>
                <option value="days">Days</option>
              </select>
            </div>
          </PanelSection>
          <div className="rounded-xl p-3 border text-xs leading-relaxed" style={{ backgroundColor: "#F7FAFC", borderColor: "#E2E8F0", color: "#718096" }}>
            Track users who complete <strong style={{ color: "#4A5568" }}>{goalSettings.event}</strong> within{" "}
            <strong style={{ color: "#4A5568" }}>{goalSettings.window} {goalSettings.windowUnit}</strong> of seeing this message.
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Template Thumbnails ──────────────────────────────────────────────────────

function TemplateThumbnail({ templateKey }: { templateKey: string }) {
  switch (templateKey) {
    case "welcome":
      return (
        <div className="w-full h-full relative overflow-hidden bg-white">
          <div className="absolute top-0 left-0 right-0 h-16 flex items-center justify-center text-2xl"
            style={{ background: "linear-gradient(135deg, var(--brand-primary), #5A67D8)" }}>👋</div>
          <div className="absolute top-16 left-0 right-0 px-3 pt-2">
            <div className="h-2.5 rounded-full mb-1.5 mx-auto" style={{ backgroundColor: "#2D3748", width: "80%" }} />
            <div className="h-1.5 rounded-full mb-1 mx-auto" style={{ backgroundColor: "#E2E8F0", width: "75%" }} />
            <div className="h-1.5 rounded-full mb-3 mx-auto" style={{ backgroundColor: "#E2E8F0", width: "65%" }} />
            <div className="h-6 rounded-lg mx-1" style={{ backgroundColor: "var(--brand-primary)" }} />
            <div className="h-1.5 rounded-full mt-2 mx-auto" style={{ backgroundColor: "#E2E8F0", width: "33%" }} />
          </div>
        </div>
      );
    case "announcement":
      return (
        <div className="w-full h-full relative overflow-hidden bg-white">
          <div className="absolute top-0 left-0 right-0 h-12 flex items-center justify-center text-xl"
            style={{ background: "linear-gradient(135deg, #38A169, #319795)" }}>⚡</div>
          <div className="absolute top-12 left-0 right-0 px-3 pt-2">
            <div className="h-2.5 rounded-full mb-1.5" style={{ backgroundColor: "#2D3748", width: "80%" }} />
            <div className="h-1.5 rounded-full mb-1" style={{ backgroundColor: "#E2E8F0", width: "75%" }} />
            <div className="h-1.5 rounded-full mb-3" style={{ backgroundColor: "#E2E8F0", width: "65%" }} />
            <div className="flex gap-2">
              <div className="flex-1 h-6 rounded-lg" style={{ backgroundColor: "#38A169" }} />
              <div className="flex-1 h-6 rounded-lg border-2" style={{ borderColor: "#38A169" }} />
            </div>
          </div>
        </div>
      );
    case "promo":
      return (
        <div className="w-full h-full relative overflow-hidden bg-white">
          <div className="absolute top-0 left-0 right-0 h-20 flex items-center justify-center text-3xl"
            style={{ background: "linear-gradient(135deg, #D69E2E, #DD6B20)" }}>💎</div>
          <div className="absolute top-20 left-0 right-0 px-3 pt-2">
            <div className="h-2.5 rounded-full mb-3 mx-auto" style={{ backgroundColor: "#2D3748", width: "75%" }} />
            <div className="flex gap-1.5 justify-center mb-3">
              {["02", "14", "37"].map((n) => (
                <div key={n} className="w-8 h-6 rounded flex items-center justify-center text-white font-bold"
                  style={{ backgroundColor: "#DD6B20", fontSize: 10 }}>{n}</div>
              ))}
            </div>
            <div className="h-5 rounded-lg" style={{ backgroundColor: "var(--brand-primary)" }} />
          </div>
        </div>
      );
    case "update":
      return (
        <div className="w-full h-full relative overflow-hidden bg-white p-3">
          <div className="flex items-center gap-2 mb-3">
            <div className="text-lg">🔄</div>
            <div>
              <div className="h-2 rounded-full w-24 mb-1" style={{ backgroundColor: "#2D3748" }} />
              <div className="h-1.5 rounded-full w-20" style={{ backgroundColor: "#E2E8F0" }} />
            </div>
          </div>
          <div className="space-y-1 mb-3">
            {[0, 1].map((r) => <div key={r} className="h-1.5 rounded-full" style={{ backgroundColor: "#F7FAFC", width: r === 0 ? "90%" : "60%" }} />)}
          </div>
          <div className="h-5 rounded-lg" style={{ backgroundColor: "#10B981" }} />
        </div>
      );
    default:
      return <div className="w-full h-full" style={{ backgroundColor: "#F7FAFC" }} />;
  }
}

// ─── Template Picker Modal ────────────────────────────────────────────────────

function TemplatePickerModal({ onSelect, onClose }: { onSelect: (key: string) => void; onClose: () => void }) {
  const [hovered, setHovered] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: "rgba(0,0,0,0.6)" }}>
      <div className="rounded-2xl shadow-2xl overflow-hidden flex flex-col" style={{ backgroundColor: "#FFFFFF", width: 680, maxHeight: "90vh" }}>
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: "#E2E8F0" }}>
          <div>
            <h2 className="text-lg font-bold" style={{ color: "#1A202C" }}>Choose a template</h2>
            <p className="text-sm mt-0.5" style={{ color: "#718096" }}>Start with a pre-built layout and customize</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg transition-colors hover:bg-gray-100">
            <X size={18} style={{ color: "#718096" }} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-3 gap-4">
            {Object.entries(TEMPLATES).map(([key, template]) => (
              <div key={key}
                className="relative rounded-xl overflow-hidden border-2 cursor-pointer transition-all hover:shadow-lg"
                style={{ borderColor: hovered === key ? "var(--brand-primary)" : "#E2E8F0" }}
                onMouseEnter={() => setHovered(key)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => onSelect(key)}>
                <div className="h-40 relative overflow-hidden" style={{ backgroundColor: "#F7FAFC" }}>
                  <TemplateThumbnail templateKey={key} />
                  {hovered === key && (
                    <div className="absolute inset-0 flex items-center justify-center" style={{ backgroundColor: "rgba(49,130,206,0.15)" }}>
                      <span className="px-3 py-1.5 rounded-full text-xs font-semibold text-white shadow-lg"
                        style={{ backgroundColor: "var(--brand-primary)" }}>
                        Use template
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <p className="text-sm font-semibold" style={{ color: "#1A202C" }}>{template.name}</p>
                  <p className="text-xs mt-0.5" style={{ color: "#718096" }}>{template.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between px-6 py-4 border-t" style={{ borderColor: "#E2E8F0" }}>
          <button className="text-sm font-medium transition-colors hover:opacity-70" style={{ color: "#4A5568" }}
            onClick={() => onSelect("blank")}>
            Start blank →
          </button>
          <button className="text-sm transition-colors hover:opacity-70" style={{ color: "#718096" }} onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Preview Modal ────────────────────────────────────────────────────────────

function PreviewModal({
  device, setDevice, blocks, messageType, messageStyle, onClose,
}: {
  device: Device;
  setDevice: (d: Device) => void;
  blocks: Block[];
  messageType: MessageType;
  messageStyle: MessageStyle;
  onClose: () => void;
}) {
  const DEVICES: Device[] = ["ios", "android"];
  const idx = DEVICES.indexOf(device);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const overlay = (
    <MessageOverlay
      messageType={messageType}
      messageStyle={messageStyle}
      blocks={blocks}
      selectedBlockId={null}
      onSelectBlock={() => {}}
      onUpdateBlock={() => {}}
      onDeleteBlock={() => {}}
      onDuplicateBlock={() => {}}
      dragOverIndex={null}
    />
  );
  const content = <div className="relative w-full h-full"><SampleAppBackground />{overlay}</div>;

  const frame = device === "android"
    ? <AndroidFrame>{content}</AndroidFrame>
    : <IPhoneFrame>{content}</IPhoneFrame>;

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center" style={{ backgroundColor: "rgba(15,15,15,0.95)" }}>
      <button onClick={onClose} className="absolute top-6 left-6 flex items-center gap-2 text-white hover:opacity-70 transition-opacity">
        <ChevronLeftIcon size={18} /> <span className="text-sm">Exit preview</span>
      </button>
      <div className="flex items-center gap-3 mb-8">
        {DEVICES.map((d) => (
          <button key={d} className="px-4 py-2 rounded-full text-sm font-medium transition-all"
            style={{ backgroundColor: device === d ? "#FFFFFF" : "rgba(255,255,255,0.1)", color: device === d ? "#1A202C" : "#FFFFFF" }}
            onClick={() => setDevice(d)}>
            {d === "ios" ? "iOS" : "Android"}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-8">
        <button className="w-10 h-10 rounded-full flex items-center justify-center text-white hover:bg-white/10 transition-colors disabled:opacity-30"
          disabled={idx === 0} onClick={() => setDevice(DEVICES[idx - 1])}>
          <ChevronLeftIcon size={20} />
        </button>
        <div style={{ transform: "scale(1.15)", transformOrigin: "center" }}>
          {frame}
        </div>
        <button className="w-10 h-10 rounded-full flex items-center justify-center text-white hover:bg-white/10 transition-colors disabled:opacity-30"
          disabled={idx === DEVICES.length - 1} onClick={() => setDevice(DEVICES[idx + 1])}>
          <ChevronRight size={20} />
        </button>
      </div>
      <div className="flex gap-2 mt-8">
        {DEVICES.map((d) => (
          <button key={d} className="rounded-full transition-all"
            style={{ width: device === d ? 20 : 6, height: 6, backgroundColor: device === d ? "#FFFFFF" : "rgba(255,255,255,0.3)" }}
            onClick={() => setDevice(d)} />
        ))}
      </div>
    </div>
  );
}

// ─── Block Icons & Labels ─────────────────────────────────────────────────────

function blockIcon(type: BlockType, size = 14) {
  const props = { size, className: "flex-shrink-0" };
  switch (type) {
    case "heading": return <Heading {...props} />;
    case "text": return <Type {...props} />;
    case "image": return <ImageIcon {...props} />;
    case "button": return <Square {...props} />;
    case "divider": return <Minus {...props} />;
    case "spacer": return <AlignLeft {...props} />;
    default: return <Square {...props} />;
  }
}

function blockLabel(block: Block): string {
  const c = block.content;
  if (c.text) return c.text.slice(0, 22);
  if (block.type === "image") return c.imageAlt ? c.imageAlt : "Image";
  return block.type.charAt(0).toUpperCase() + block.type.slice(1);
}

// ─── Palette Groups ───────────────────────────────────────────────────────────

const PALETTE_GROUPS: { label: string; items: { type: BlockType; label: string }[] }[] = [
  {
    label: "Content",
    items: [
      { type: "text", label: "Text" },
      { type: "heading", label: "Heading" },
      { type: "image", label: "Image" },
      { type: "button", label: "Button" },
      { type: "divider", label: "Divider" },
      { type: "spacer", label: "Spacer" },
    ],
  },
];

// ─── Status Badge ─────────────────────────────────────────────────────────────

type MessageStatus = "draft" | "active" | "paused" | "archived";

const STATUS_BADGE: Record<MessageStatus, { label: string; bg: string; fg: string }> = {
  draft: { label: "Draft", bg: "var(--brand-primary)", fg: "#FFFFFF" },
  active: { label: "Active", bg: "#C6F6D5", fg: "#22543D" },
  paused: { label: "Paused", bg: "#FEEBC8", fg: "#7B341E" },
  archived: { label: "Archived", bg: "#E2E8F0", fg: "#4A5568" },
};

// ─── Main Component ───────────────────────────────────────────────────────────

export function InAppDesigner({ onBack }: { onBack?: () => void }) {
  const { id: routeId } = useParams();
  const navigate = useNavigate();

  const [messageId, setMessageId] = useState<string | null>(routeId ?? null);
  const [status, setStatus] = useState<MessageStatus>("draft");
  const [loading, setLoading] = useState<boolean>(!!routeId);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [toast, setToast] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = (type: "success" | "error", text: string) => {
    setToast({ type, text });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 2600);
  };

  const [blocks, setBlocks] = useState<Block[]>(() =>
    TEMPLATES.welcome.blocks.map(b => ({ ...b, id: uid() }))
  );
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<MessageType>("modal");
  const [device, setDevice] = useState<Device>("ios");
  const [messageStyle, setMessageStyle] = useState<MessageStyle>(
    coerceStyleForSurface(
      { background: "#FFFFFF", cornerRadius: 16, backdropOpacity: 40, animation: "slide-up", position: "center",
        closeButton: true, tapBackdrop: true, swipeToDismiss: false, autoDismissSecs: 0 },
      "modal",
    )
  );
  const [triggerSettings, setTriggerSettings] = useState<TriggerSettings>({ event: "app_open", delay: 0, delayUnit: "seconds" });
  const [audienceSettings, setAudienceSettings] = useState<AudienceSettings>({ type: "all" });
  const [frequencySettings, setFrequencySettings] = useState<FrequencySettings>({ type: "once", startDate: "2026-07-20", endDate: "2026-08-20" });
  const [goalSettings, setGoalSettings] = useState<GoalSettings>({ event: "purchase", window: 24, windowUnit: "hours" });
  const [messageName, setMessageName] = useState("Welcome — July");
  const [editingName, setEditingName] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [propTab, setPropTab] = useState<PropTab>("style");

  const draggedTypeRef = useRef<BlockType | null>(null);
  const draggedBlockIdRef = useRef<string | null>(null);

  // History
  const [history, setHistory] = useState<Block[][]>([blocks]);
  const [historyIdx, setHistoryIdx] = useState(0);

  // Load an existing message when the route carries an :id
  useEffect(() => {
    if (!routeId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const { data: res } = await axios.get(`${API_URL}/${routeId}`, { headers: getHeaders() });
        const msg = res?.data;
        if (!msg) throw new Error("not found");
        if (cancelled) return;

        setMessageId(msg.id ?? routeId);
        setMessageName(msg.name ?? "");
        setMessageType(msg.message_type ?? "modal");

        const loadedBlocks: Block[] = msg.design?.blocks ?? [];
        setBlocks(loadedBlocks);
        setHistory([loadedBlocks]);
        setHistoryIdx(0);

        const loadedType: MessageType = msg.message_type ?? "modal";
        const st = msg.design?.style ?? {};
        setMessageStyle(coerceStyleForSurface({
          background: st.background ?? "#FFFFFF",
          cornerRadius: st.cornerRadius ?? 16,
          backdropOpacity: st.backdropOpacity ?? 40,
          animation: st.animation ?? "slide-up",
          position: st.position ?? "center",
          closeButton: st.closeButton ?? st.close_button ?? true,
          tapBackdrop: st.tapBackdrop ?? st.tap_backdrop ?? false,
          swipeToDismiss: st.swipeToDismiss ?? st.swipe_to_dismiss ?? false,
          autoDismissSecs: st.autoDismissSecs ?? st.auto_dismiss_secs ?? 0,
        }, loadedType));
        setTriggerSettings({
          event: msg.trigger?.event ?? "app_open",
          delay: msg.trigger?.delay ?? 0,
          delayUnit: msg.trigger?.delay_unit ?? "seconds",
        });
        setAudienceSettings({
          type: msg.audience?.type === "cohort" ? "cohort" : "all",
          cohortId: msg.audience?.cohort_id ?? undefined,
          cohortName: msg.audience?.cohort_name ?? undefined,
          estimatedReach: msg.audience?.estimated_reach ?? undefined,
        });
        setFrequencySettings({
          type: msg.frequency?.type ?? "once",
          maxTimes: msg.frequency?.max_times ?? undefined,
          startDate: msg.frequency?.start_date ?? "",
          endDate: msg.frequency?.end_date ?? "",
        });
        setGoalSettings({
          event: msg.goal?.event ?? "purchase",
          window: msg.goal?.window ?? 24,
          windowUnit: msg.goal?.window_unit ?? "hours",
        });
        setStatus(msg.status ?? "draft");
      } catch {
        if (!cancelled) setLoadError("Couldn't load this message.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId]);

  const buildPayload = () => ({
    name: messageName,
    message_type: messageType,
    design: { blocks, style: messageStyle },
    trigger: {
      event: triggerSettings.event,
      delay: triggerSettings.delay,
      delay_unit: triggerSettings.delayUnit,
    },
    audience: {
      type: audienceSettings.type,
      cohort_id: audienceSettings.type === "cohort" ? (audienceSettings.cohortId ?? null) : null,
      estimated_reach: audienceSettings.estimatedReach ?? null,
    },
    frequency: {
      type: frequencySettings.type,
      max_times: frequencySettings.type === "max" ? (frequencySettings.maxTimes ?? null) : null,
      start_date: frequencySettings.startDate,
      end_date: frequencySettings.endDate,
    },
    goal: {
      event: goalSettings.event,
      window: goalSettings.window,
      window_unit: goalSettings.windowUnit,
    },
  });

  // Create on first save, update thereafter
  const persist = async () => {
    const payload = buildPayload();
    if (messageId) {
      const { data: res } = await axios.put(`${API_URL}/${messageId}`, payload, { headers: getHeaders() });
      return res?.data;
    }
    const { data: res } = await axios.post(API_URL, payload, { headers: getHeaders() });
    const saved = res?.data;
    if (saved?.id) {
      setMessageId(saved.id);
      navigate(`/tools/in-app-designer/${saved.id}`, { replace: true });
    }
    return saved;
  };

  const handleSave = async () => {
    if (saving || publishing) return;
    setSaving(true);
    try {
      const saved = await persist();
      if (saved?.status) setStatus(saved.status);
      showToast("success", "Draft saved");
    } catch {
      showToast("error", "Couldn't save — try again");
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (saving || publishing) return;
    const blockers = validationIssues();
    if (blockers.length) {
      showToast("error", blockers.length === 1 ? blockers[0] : `Fix ${blockers.length} issues before publishing`);
      return;
    }
    setPublishing(true);
    try {
      const saved = await persist();
      const id = saved?.id ?? messageId;
      if (!id) throw new Error("missing id");
      const { data: res } = await axios.post(`${API_URL}/${id}/publish`, {}, { headers: getHeaders() });
      setStatus(res?.data?.status ?? "active");
      showToast("success", "Message published");
    } catch {
      showToast("error", "Couldn't publish — try again");
    } finally {
      setPublishing(false);
    }
  };

  // BUG-13 — accessible reorder without dragging. Moves a block one slot up/down.
  const moveBlock = (idx: number, dir: -1 | 1) => {
    const to = idx + dir;
    if (to < 0 || to >= blocks.length) return;
    const next = [...blocks];
    [next[idx], next[to]] = [next[to], next[idx]];
    updateBlocks(next);
  };

  // BUG-12 — undo/redo preserve the current selection when the block still exists,
  // so the user doesn't get bounced back to Message Settings and lose their place.
  const keepSelection = (nextBlocks: Block[]) =>
    setSelectedBlockId(id => (id && nextBlocks.some(b => b.id === id) ? id : null));

  const undo = () => {
    if (historyIdx > 0) {
      const prev = historyIdx - 1;
      setHistoryIdx(prev);
      setBlocks(history[prev]);
      keepSelection(history[prev]);
    }
  };
  const redo = () => {
    if (historyIdx < history.length - 1) {
      const next = historyIdx + 1;
      setHistoryIdx(next);
      setBlocks(history[next]);
      keepSelection(history[next]);
    }
  };

  const updateBlocks = useCallback((newBlocks: Block[]) => {
    setBlocks(newBlocks);
    setHistory(prev => [...prev.slice(0, historyIdx + 1), newBlocks]);
    setHistoryIdx(prev => prev + 1);
  }, [historyIdx]);

  const updateBlock = (id: string, content: Partial<BlockContent>) => {
    setBlocks(prev => prev.map(b => b.id === id ? { ...b, content: { ...b.content, ...content } } : b));
  };

  const deleteBlock = (id: string) => {
    if (selectedBlockId === id) setSelectedBlockId(null);
    updateBlocks(blocks.filter(b => b.id !== id));
  };

  const duplicateBlock = (id: string) => {
    const idx = blocks.findIndex(b => b.id === id);
    if (idx === -1) return;
    const copy = { ...blocks[idx], id: uid() };
    const newBlocks = [...blocks.slice(0, idx + 1), copy, ...blocks.slice(idx + 1)];
    updateBlocks(newBlocks);
    setSelectedBlockId(copy.id);
  };

  const surfaceCfg = SURFACE_CONFIG[messageType];

  // BUG-01 — clicking a palette tile appends the block (the action most users try
  // first). Blocks the surface doesn't support are rejected with a clear message.
  const addBlock = (type: BlockType) => {
    if (!surfaceCfg.allowedBlocks.includes(type)) {
      showToast("error", `${type.charAt(0).toUpperCase() + type.slice(1)} isn't supported on ${messageType}.`);
      return;
    }
    const newBlock: Block = { id: uid(), type, content: defaultBlockContent(type) };
    updateBlocks([...blocks, newBlock]);
    setSelectedBlockId(newBlock.id);
  };

  // BUG-04 — switching surface must not silently drop unsupported blocks. Warn and
  // let the user confirm; coerce style onto the new surface's allowed options.
  const changeSurface = (type: MessageType) => {
    if (type === messageType) return;
    const cfg = SURFACE_CONFIG[type];
    const dropped = [...new Set(blocks.filter(b => !cfg.allowedBlocks.includes(b.type)).map(b => b.type))];
    if (dropped.length && !window.confirm(
      `${type} doesn't support: ${dropped.join(", ")}. Those blocks won't be shown on this surface. Switch anyway?`
    )) return;
    setMessageType(type);
    setMessageStyle(prev => coerceStyleForSurface(prev, type));
  };

  const selectTemplate = (key: string) => {
    if (key === "blank") {
      updateBlocks([]);
      setSelectedBlockId(null);
    } else {
      const t = TEMPLATES[key];
      if (t) {
        updateBlocks(t.blocks.map(b => ({ ...b, id: uid() })));
        setMessageType(t.type);
        setMessageStyle(prev => coerceStyleForSurface(prev, t.type));
        setSelectedBlockId(null);
      }
    }
    setShowTemplatePicker(false);
  };

  // BUG-02 / BUG-07 — publish is gated on a real validation pass.
  const validationIssues = (): string[] => {
    const out: string[] = [];
    if (blocks.length === 0) out.push("Add at least one block.");
    blocks.forEach((b) => {
      if ((b.type === "heading" || b.type === "text") && !(b.content.text || "").trim()) out.push(`Empty ${b.type} block.`);
      if (b.type === "button") {
        if (!(b.content.text || "").trim()) out.push("A button is missing its label.");
        if ((b.content.action === "openUrl" || b.content.action === "deepLink") && !(b.content.actionUrl || "").trim()) out.push("A button action is missing its URL.");
        if (b.content.action === "deepLink" && !(b.content.actionFallbackUrl || "").trim()) out.push("A deep-link button is missing its fallback URL.");
      }
      if (b.type === "image" && b.content.imageUrl && !(b.content.imageAlt || "").trim()) out.push("An image is missing alt text.");
    });
    if (!hasExitPath(messageStyle, messageType)) out.push(`This ${messageType} has no way to be dismissed.`);
    // BUG-08 — contrast. Skip when a background image covers the surface (can't be
    // evaluated statically). Otherwise text must clear WCAG AA against the background.
    const bgImage = blocks.find(b => b.type === "image")?.content.imageUrl;
    const evaluableBg = messageType === "fullscreen" ? (bgImage ? null : messageStyle.background) : messageStyle.background;
    if (evaluableBg) {
      blocks.forEach((b) => {
        if (textBlockContrastFails(b, evaluableBg)) {
          out.push(`Low contrast: "${(b.content.text || "").slice(0, 18)}…" is hard to read on this background.`);
        }
      });
    }
    if (messageType === "banner") {
      const h = blocks.find(b => b.type === "heading")?.content.text || "";
      const t = blocks.find(b => b.type === "text")?.content.text || "";
      const bl = blocks.find(b => b.type === "button")?.content.text || "";
      if (h.length > BANNER_LIMITS.heading) out.push(`Banner title exceeds ${BANNER_LIMITS.heading} characters — it will be truncated.`);
      if (t.length > BANNER_LIMITS.text) out.push(`Banner body exceeds ${BANNER_LIMITS.text} characters — it will be truncated.`);
      if (bl.length > BANNER_LIMITS.button) out.push(`Banner button label exceeds ${BANNER_LIMITS.button} characters.`);
    }
    return out;
  };
  const issues = validationIssues();

  const handlePaletteDragStart = (e: React.DragEvent, type: BlockType) => {
    draggedTypeRef.current = type;
    draggedBlockIdRef.current = null;
    // Setting drag data makes the drop fire reliably across browsers.
    try { e.dataTransfer.setData("text/plain", type); e.dataTransfer.effectAllowed = "copy"; } catch { /* noop */ }
  };

  const handleOutlineDragStart = (id: string) => {
    draggedBlockIdRef.current = id;
    draggedTypeRef.current = null;
  };

  const handleOutlineDrop = (targetIdx: number) => {
    if (!draggedBlockIdRef.current) return;
    const fromIdx = blocks.findIndex(b => b.id === draggedBlockIdRef.current);
    if (fromIdx === -1) return;
    const newBlocks = [...blocks];
    const [removed] = newBlocks.splice(fromIdx, 1);
    newBlocks.splice(targetIdx > fromIdx ? targetIdx - 1 : targetIdx, 0, removed);
    updateBlocks(newBlocks);
    draggedBlockIdRef.current = null;
  };

  const handleCanvasDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const y = e.clientY - rect.top;
    const idx = Math.min(Math.round(y / Math.max(rect.height / (blocks.length || 1), 1)), blocks.length);
    setDragOverIndex(idx);
  };

  const handleCanvasDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (draggedTypeRef.current) {
      const type = draggedTypeRef.current;
      if (!surfaceCfg.allowedBlocks.includes(type)) {
        showToast("error", `${type.charAt(0).toUpperCase() + type.slice(1)} isn't supported on ${messageType}.`);
      } else {
        const newBlock: Block = { id: uid(), type, content: defaultBlockContent(type) };
        const idx = dragOverIndex ?? blocks.length;
        const newBlocks = [...blocks.slice(0, idx), newBlock, ...blocks.slice(idx)];
        updateBlocks(newBlocks);
        setSelectedBlockId(newBlock.id);
      }
    }
    draggedTypeRef.current = null;
    draggedBlockIdRef.current = null;
    setDragOverIndex(null);
  };

  const selectedBlock = blocks.find(b => b.id === selectedBlockId) || null;

  const MESSAGE_TYPES: { id: MessageType; label: string }[] = [
    { id: "modal", label: "Modal" },
    { id: "banner", label: "Banner" },
    { id: "slide-in", label: "Slide-in" },
    { id: "fullscreen", label: "Fullscreen" },
  ];

  const renderDeviceContent = () => (
    <div className="relative w-full h-full" onClick={() => setSelectedBlockId(null)}>
      <SampleAppBackground />
      <div
        className="absolute inset-0"
        onDragOver={handleCanvasDragOver}
        onDrop={handleCanvasDrop}
        onDragLeave={() => setDragOverIndex(null)}
      >
        <MessageOverlay
          messageType={messageType}
          messageStyle={messageStyle}
          blocks={blocks}
          selectedBlockId={selectedBlockId}
          onSelectBlock={setSelectedBlockId}
          onUpdateBlock={updateBlock}
          onDeleteBlock={deleteBlock}
          onDuplicateBlock={duplicateBlock}
          dragOverIndex={dragOverIndex}
        />
      </div>
    </div>
  );

  const deviceFrame = device === "android"
    ? <AndroidFrame>{renderDeviceContent()}</AndroidFrame>
    : <IPhoneFrame>{renderDeviceContent()}</IPhoneFrame>;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-3" style={{ backgroundColor: "#F7FAFC" }}>
        <div className="w-8 h-8 rounded-full border-2 animate-spin" style={{ borderColor: "var(--brand-border)", borderTopColor: "var(--brand-primary)" }} />
        <p className="text-sm" style={{ color: "#718096" }}>Loading message…</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-3" style={{ backgroundColor: "#F7FAFC" }}>
        <p className="text-sm font-medium" style={{ color: "#E53E3E" }}>{loadError}</p>
        <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors hover:bg-gray-50"
          style={{ borderColor: "#E2E8F0", color: "#4A5568" }} onClick={onBack}>
          <ChevronLeftIcon size={15} /> Go back
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden" style={{ fontFamily: "Inter, system-ui, sans-serif", backgroundColor: "#F7FAFC" }}>

      {/* Top Bar */}
      <div className="flex items-center px-4 gap-3 flex-shrink-0 border-b"
        style={{ height: 56, backgroundColor: "#FFFFFF", borderColor: "#E2E8F0" }}>
        {/* Left */}
        <div className="flex items-center gap-2 flex-shrink-0" style={{ width: 260 }}>
          <button className="p-1.5 rounded-lg transition-colors hover:bg-gray-100" style={{ color: "#718096" }} onClick={onBack}>
            <ChevronLeft size={18} />
          </button>
          <div className="flex items-center gap-2 min-w-0">
            {editingName ? (
              <input autoFocus
                className="text-sm font-semibold border-b-2 bg-transparent outline-none"
                style={{ color: "#1A202C", borderColor: "var(--brand-primary)", maxWidth: 160 }}
                value={messageName}
                onChange={(e) => setMessageName(e.target.value)}
                onBlur={() => setEditingName(false)}
                onKeyDown={(e) => { if (e.key === "Enter") setEditingName(false); }} />
            ) : (
              <button className="text-sm font-semibold truncate hover:opacity-70 transition-opacity"
                style={{ color: "#1A202C", maxWidth: 160 }} onClick={() => setEditingName(true)}>
                {messageName}
              </button>
            )}
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0"
              style={{ backgroundColor: STATUS_BADGE[status].bg, color: STATUS_BADGE[status].fg }}>
              {STATUS_BADGE[status].label}
            </span>
          </div>
        </div>

        {/* Center — Message type */}
        <div className="flex-1 flex justify-center">
          <div className="flex gap-0.5 p-0.5 rounded-xl" style={{ backgroundColor: "#F7FAFC", border: "1px solid #E2E8F0" }}>
            {MESSAGE_TYPES.map((t) => (
              <button key={t.id}
                className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                style={{
                  backgroundColor: messageType === t.id ? "var(--brand-primary)" : "transparent",
                  color: messageType === t.id ? "#FFFFFF" : "#718096",
                }}
                onClick={() => changeSurface(t.id)}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Right */}
        <div className="flex items-center gap-2 justify-end flex-shrink-0" style={{ width: 260 }}>
          <div className="flex gap-0.5 p-0.5 rounded-lg" style={{ backgroundColor: "#F7FAFC", border: "1px solid #E2E8F0" }}>
            {(["ios", "android"] as Device[]).map((d) => (
              <button key={d}
                className="p-1.5 rounded-md transition-all"
                style={{ backgroundColor: device === d ? "#FFFFFF" : "transparent", color: device === d ? "var(--brand-primary)" : "#718096" }}
                onClick={() => setDevice(d)} title={d === "ios" ? "iOS" : "Android"}>
                <Smartphone size={15} />
              </button>
            ))}
          </div>
          <button className="p-1.5 rounded-lg transition-colors hover:bg-gray-100 disabled:opacity-30"
            style={{ color: "#A0AEC0" }} onClick={undo} disabled={historyIdx === 0}><Undo2 size={16} /></button>
          <button className="p-1.5 rounded-lg transition-colors hover:bg-gray-100 disabled:opacity-30"
            style={{ color: "#A0AEC0" }} onClick={redo} disabled={historyIdx >= history.length - 1}><Redo2 size={16} /></button>
          <button
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors hover:bg-gray-50"
            style={{ borderColor: "#E2E8F0", color: "#4A5568" }}
            onClick={() => setShowPreview(true)}>
            <Eye size={15} /> Preview
          </button>
          <button
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors disabled:opacity-50"
            style={{ borderColor: "#E2E8F0", color: "#4A5568" }}
            onMouseEnter={(e) => { if (saving || publishing) return; const el = e.currentTarget as HTMLElement; el.style.borderColor = "var(--brand-border)"; el.style.color = "var(--brand-primary)"; el.style.backgroundColor = "var(--brand-tint)"; }}
            onMouseLeave={(e) => { const el = e.currentTarget as HTMLElement; el.style.borderColor = "#E2E8F0"; el.style.color = "#4A5568"; el.style.backgroundColor = "transparent"; }}
            onClick={handleSave} disabled={saving || publishing}>
            <SaveIcon size={15} /> {saving ? "Saving…" : "Save"}
          </button>
          {issues.length > 0 && (
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0"
              title={issues.join("\n")}
              style={{ backgroundColor: "#FED7D7", color: "#C53030" }}>
              {issues.length} {issues.length === 1 ? "issue" : "issues"}
            </span>
          )}
          <button
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold text-white transition-all disabled:opacity-50"
            style={{ backgroundColor: "var(--brand-primary)", boxShadow: "0 2px 8px rgb(var(--brand-primary-rgb) / 0.35)" }}
            title={issues.length ? issues.join("\n") : undefined}
            onMouseEnter={(e) => { if (saving || publishing) return; (e.currentTarget as HTMLElement).style.backgroundColor = "var(--brand-primary-hover)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "var(--brand-primary)"; }}
            onClick={handlePublish} disabled={saving || publishing}>
            <Send size={14} /> {publishing ? "Publishing…" : status === "active" ? "Republish" : "Publish"}
          </button>
        </div>
      </div>

      {/* Main Layout */}
      <div className="flex flex-1 overflow-hidden">

        {/* Left Panel */}
        <div className="flex-shrink-0 flex flex-col border-r overflow-hidden" style={{ width: 248, backgroundColor: "#FFFFFF", borderColor: "#E2E8F0" }}>
          {/* Templates button */}
          <div className="px-3 py-2.5 border-b" style={{ borderColor: "#E2E8F0" }}>
            <button
              className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border-2  text-sm font-medium transition-colors"
              style={{ borderColor: "#CBD5E0", color: "#718096" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "#93C5FD"; (e.currentTarget as HTMLElement).style.color = "var(--brand-primary)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "#CBD5E0"; (e.currentTarget as HTMLElement).style.color = "#718096"; }}
              onClick={() => setShowTemplatePicker(true)}>
              <Layers size={14} /> Templates
            </button>
          </div>

          {/* Block Palette */}
          <div className="overflow-y-auto flex-shrink-0" style={{ maxHeight: "44%" }}>
            <div className="px-3 pt-3">
              {PALETTE_GROUPS.map((group) => (
                <div key={group.label} className="mb-3">
                  <p className="text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: "#A0AEC0" }}>{group.label}</p>
                  <div className="grid grid-cols-3 gap-1.5">
                    {group.items.map((item) => {
                      const supported = surfaceCfg.allowedBlocks.includes(item.type);
                      return (
                        <button key={item.type} type="button" draggable={supported}
                          disabled={!supported}
                          title={supported ? `Add ${item.label}` : `${item.label} isn't supported on ${messageType}`}
                          onDragStart={(e) => handlePaletteDragStart(e, item.type)}
                          onClick={() => addBlock(item.type)}
                          className="flex flex-col items-center gap-1 py-2 px-1 rounded-lg border transition-colors focus:outline-none focus:ring-2 disabled:opacity-40 disabled:cursor-not-allowed"
                          style={{ backgroundColor: "#F7FAFC", borderColor: "#E2E8F0", cursor: supported ? "pointer" : "not-allowed" }}
                          onMouseEnter={(e) => { if (!supported) return; const el = e.currentTarget as HTMLElement; el.style.borderColor = "#93C5FD"; el.style.backgroundColor = "var(--brand-tint)"; }}
                          onMouseLeave={(e) => { const el = e.currentTarget as HTMLElement; el.style.borderColor = "#E2E8F0"; el.style.backgroundColor = "#F7FAFC"; }}>
                          <div style={{ color: "#718096" }}>{blockIcon(item.type, 16)}</div>
                          <span style={{ color: "#4A5568", fontSize: 10 }}>{item.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Divider */}
          <div className="border-t mx-3" style={{ borderColor: "#E2E8F0" }} />

          {/* Block Outline */}
          <div className="flex-1 overflow-y-auto px-3 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: "#A0AEC0" }}>Blocks in message</p>
            {blocks.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-xs" style={{ color: "#CBD5E0" }}>No blocks yet.</p>
                <p className="text-xs" style={{ color: "#CBD5E0" }}>Drag from palette above.</p>
              </div>
            ) : (
              <div className="space-y-1" role="list">
                {blocks.map((block, idx) => (
                  <div key={block.id} role="listitem" draggable
                    onDragStart={() => handleOutlineDragStart(block.id)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => handleOutlineDrop(idx)}
                    className="flex items-center gap-1 px-1.5 py-1 rounded-lg transition-all group border"
                    style={{
                      backgroundColor: selectedBlockId === block.id ? "var(--brand-tint)" : "transparent",
                      borderColor: selectedBlockId === block.id ? "var(--brand-border)" : "transparent",
                    }}>
                    <GripVertical size={12} className="flex-shrink-0 cursor-grab" style={{ color: "#CBD5E0" }} />
                    {/* Selecting the block is a real, focusable button (BUG-13). */}
                    <button type="button"
                      className="flex-1 flex items-center gap-2 min-w-0 text-left rounded focus:outline-none focus:ring-2"
                      onClick={() => setSelectedBlockId(block.id)}>
                      <span style={{ color: selectedBlockId === block.id ? "var(--brand-primary)" : "#718096" }}>
                        {blockIcon(block.type, 12)}
                      </span>
                      <span className="flex-1 text-xs truncate" style={{ color: selectedBlockId === block.id ? "var(--brand-primary-hover)" : "#4A5568" }}>
                        {blockLabel(block)}
                      </span>
                    </button>
                    {/* Keyboard-accessible reorder + delete (BUG-13). */}
                    <button type="button" title="Move up" aria-label="Move block up" disabled={idx === 0}
                      className="p-0.5 rounded focus:outline-none focus:ring-2 disabled:opacity-30"
                      style={{ color: "#A0AEC0" }}
                      onClick={(e) => { e.stopPropagation(); moveBlock(idx, -1); }}>
                      <ChevronUp size={12} />
                    </button>
                    <button type="button" title="Move down" aria-label="Move block down" disabled={idx === blocks.length - 1}
                      className="p-0.5 rounded focus:outline-none focus:ring-2 disabled:opacity-30"
                      style={{ color: "#A0AEC0" }}
                      onClick={(e) => { e.stopPropagation(); moveBlock(idx, 1); }}>
                      <ChevronDown size={12} />
                    </button>
                    <button type="button" title="Delete block" aria-label="Delete block"
                      className="p-0.5 rounded focus:outline-none focus:ring-2"
                      style={{ color: "#CBD5E0" }}
                      onMouseEnter={(e) => (e.currentTarget as HTMLElement).style.color = "#FC8181"}
                      onMouseLeave={(e) => (e.currentTarget as HTMLElement).style.color = "#CBD5E0"}
                      onClick={(e) => { e.stopPropagation(); deleteBlock(block.id); }}>
                      <Trash2 size={11} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Canvas */}
        <div
          className="flex-1 flex items-center justify-center overflow-auto"
          style={{ background: "radial-gradient(circle, #e2e8f0 1px, transparent 1px) 0 0 / 20px 20px, #f7fafc" }}
          onClick={() => setSelectedBlockId(null)}
        >
          <div className="py-8" onClick={(e) => e.stopPropagation()}>
            {deviceFrame}
          </div>
        </div>

        {/* Right Panel */}
        <div className="flex-shrink-0 border-l overflow-y-auto" style={{ width: 336, backgroundColor: "#FFFFFF", borderColor: "#E2E8F0" }}>
          {selectedBlock ? (
            <div>
              <div className="flex items-center justify-between px-5 py-3.5 border-b" style={{ borderColor: "#E2E8F0" }}>
                <div className="flex items-center gap-2">
                  <span style={{ color: "var(--brand-primary)" }}>{blockIcon(selectedBlock.type, 14)}</span>
                  <span className="text-sm font-semibold capitalize" style={{ color: "#1A202C" }}>{selectedBlock.type}</span>
                </div>
                <div className="flex items-center gap-1">
                  <button className="p-1.5 rounded-lg transition-colors hover:bg-gray-100" style={{ color: "#A0AEC0" }}
                    onClick={() => duplicateBlock(selectedBlock.id)}><Copy size={14} /></button>
                  <button className="p-1.5 rounded-lg transition-colors hover:bg-red-50" style={{ color: "#A0AEC0" }}
                    onMouseEnter={(e) => (e.currentTarget as HTMLElement).style.color = "#FC8181"}
                    onMouseLeave={(e) => (e.currentTarget as HTMLElement).style.color = "#A0AEC0"}
                    onClick={() => deleteBlock(selectedBlock.id)}><Trash2 size={14} /></button>
                  <button className="p-1.5 rounded-lg transition-colors hover:bg-gray-100" style={{ color: "#A0AEC0" }}
                    onClick={() => setSelectedBlockId(null)}><X size={14} /></button>
                </div>
              </div>
              <div className="p-5">
                <BlockPropertyPanel block={selectedBlock} onUpdate={(c) => updateBlock(selectedBlock.id, c)}
                  background={messageType === "fullscreen" && blocks.some(b => b.type === "image" && b.content.imageUrl) ? null : messageStyle.background} />
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-2 px-5 py-3.5 border-b" style={{ borderColor: "#E2E8F0" }}>
                <Settings size={14} style={{ color: "var(--brand-primary)" }} />
                <p className="text-sm font-semibold" style={{ color: "#1A202C" }}>Message Settings</p>
              </div>
              <div className="p-5">
                <MessageSettingsPanel
                  propTab={propTab} setPropTab={setPropTab}
                  messageType={messageType}
                  messageStyle={messageStyle} setMessageStyle={setMessageStyle}
                  triggerSettings={triggerSettings} setTriggerSettings={setTriggerSettings}
                  audienceSettings={audienceSettings} setAudienceSettings={setAudienceSettings}
                  frequencySettings={frequencySettings} setFrequencySettings={setFrequencySettings}
                  goalSettings={goalSettings} setGoalSettings={setGoalSettings}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showTemplatePicker && <TemplatePickerModal onSelect={selectTemplate} onClose={() => setShowTemplatePicker(false)} />}
      {showPreview && (
        <PreviewModal
          device={device} setDevice={setDevice}
          blocks={blocks} messageType={messageType} messageStyle={messageStyle}
          onClose={() => setShowPreview(false)}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium"
          style={{
            backgroundColor: toast.type === "success" ? "#1A202C" : "#C53030",
            color: "#FFFFFF",
          }}>
          {toast.text}
        </div>
      )}
    </div>
  );
}

export default InAppDesigner;
